import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Centros from "@/pages/Centros";
import Regiones from "@/pages/Regiones";
import RegionDetail from "@/pages/RegionDetail";
import Ciudades from "@/pages/Ciudades";
import CityDetail from "@/pages/CityDetail";
import Santiago from "@/pages/Santiago";
import ComunaDetail from "@/pages/ComunaDetail";
import Modalidades from "@/pages/Modalidades";
import ModalityDetail from "@/pages/ModalityDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Nosotros from "@/pages/Nosotros";
import NotFound from "@/pages/NotFound";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function NormalizeUrl() {
    // SEO hygiene: normalize URL variants to the canonical form on the client
    // so users who land on /Centros/, /centros?foo=bar, etc. see the clean URL
    // in the address bar. Uses history.replaceState to avoid extra history
    // entries. Server-side 301 redirects (Cloudflare rules) still recommended
    // for crawlers.
    const { pathname, search } = useLocation();
    useEffect(() => {
        let nextPath = pathname;
        // Strip trailing slash (except root)
        if (nextPath.length > 1 && nextPath.endsWith("/")) {
            nextPath = nextPath.replace(/\/+$/, "");
        }
        // Lowercase path (slugs are lowercase)
        if (nextPath !== nextPath.toLowerCase()) {
            nextPath = nextPath.toLowerCase();
        }
        // Strip noisy query params that generate duplicates in GSC
        const params = new URLSearchParams(search);
        const noisy = ["fbclid", "gclid", "msclkid", "_ga", "_gl", "mc_cid", "mc_eid"];
        let changedQuery = false;
        noisy.forEach((k) => {
            if (params.has(k)) {
                params.delete(k);
                changedQuery = true;
            }
        });
        const cleanSearch = params.toString() ? `?${params.toString()}` : "";
        if (nextPath !== pathname || changedQuery) {
            window.history.replaceState(
                null,
                "",
                `${nextPath}${changedQuery ? cleanSearch : search}`,
            );
        }
    }, [pathname, search]);
    return null;
}

function CleanupDefaultHead() {
    // react-helmet-async inserts new <title>/<meta> tags without removing the
    // ones that existed in the static public/index.html. This removes the
    // pre-rendered defaults on first mount so we never end up with duplicate
    // <title> tags (which is bad for SEO).
    useEffect(() => {
        const defaults = document.querySelectorAll('[data-default-title="true"]');
        defaults.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    }, []);
    return null;
}

function Shell() {
    return (
        <div className="flex min-h-screen flex-col bg-slate-950 text-slate-50">
            <Header />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/centros" element={<Centros />} />
                    <Route path="/regiones" element={<Regiones />} />
                    <Route path="/regiones/:slug" element={<RegionDetail />} />
                    <Route path="/ciudades" element={<Ciudades />} />
                    <Route path="/ciudades/:slug" element={<CityDetail />} />
                    <Route path="/santiago" element={<Santiago />} />
                    <Route path="/santiago/:slug" element={<ComunaDetail />} />
                    <Route path="/modalidades" element={<Modalidades />} />
                    <Route path="/modalidades/:slug" element={<ModalityDetail />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/nosotros" element={<Nosotros />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <ScrollToTop />
                    <NormalizeUrl />
                    <CleanupDefaultHead />
                    <Shell />
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
